import { checkEmptyValue, isNotEmpty } from '@app/utils/StringUtils';
import { uniq } from 'lodash-es';
import { FieldValues } from 'react-hook-form';

export type CorrelationErrors = Map<string, string>;
export type ListCorrelationErrors = {
  name: string;
  message: string;
};

class CorrelationCheckErrors<TFieldValues extends FieldValues = FieldValues> {
  private readonly _errors!: CorrelationErrors;

  constructor(errors?: CorrelationErrors) {
    this._errors = errors ? errors : new Map([]);
  }

  set(code: keyof TFieldValues, message: string): void {
    if (isNotEmpty(code as string)) {
      this._errors.set(code as string, message ?? '');
    }
  }

  get(code: keyof TFieldValues): string {
    if (isNotEmpty(code as string) && this._errors.has(code as string)) {
      return checkEmptyValue(this._errors.get(code as string));
    }

    return '';
  }

  delete(code: keyof TFieldValues): void {
    if (isNotEmpty(code as string) && this._errors.has(code as string)) {
      this._errors.delete(code as string);
    }
  }

  hasError() {
    return this._errors.size > 0;
  }

  getFieldErrors() {
    const listErrors: ListCorrelationErrors[] = [];
    if (this._errors.size > 0) {
      this._errors.forEach((_: string, name: string) => {
        listErrors.push({ name, message: '' });
      });
    }
    return listErrors;
  }

  getAllErrors() {
    const listErrors: ListCorrelationErrors[] = [];
    if (this._errors.size > 0) {
      this._errors.forEach((message: string, name: string) => {
        listErrors.push({ name, message });
      });
    }
    return listErrors;
  }

  toString() {
    return uniq(
      this.getAllErrors()
        .filter(({ message }) => isNotEmpty(message))
        .map(({ message }) => message)
    );
  }

  clearAllErrors() {
    this._errors.clear();
  }
}

export { CorrelationCheckErrors };
